import { Link } from 'react-router-dom'
import { ArrowRight, MessageSquare, Plus } from 'lucide-react'
import { AppShell } from '~/components/layout/app-shell'
import { EmptyState } from '~/components/shared/empty-state'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Badge } from '~/components/ui/badge'
import { listChatSessions } from '~/api/chat-history'
import { useAsyncResource } from '~/hooks/use-async'
import { formatDateTime } from '~/lib/date-utils'

/** 내 상담 이력. GET /chat/sessions?profile=consultation 목록을 카드로 보여준다. */
export function ConsultationHistoryPage() {
    const { data, loading, error } = useAsyncResource(
        () => listChatSessions('consultation'),
        [],
        '상담 이력을 불러오지 못했어요.'
    )
    // 로딩 중에는 null, 끝나면 데이터(실패해도 빈 목록으로 폴백).
    const items = loading ? null : (data ?? [])

    return (
        <AppShell>
            <div className='mx-auto h-full w-full max-w-3xl overflow-y-auto px-4 py-6'>
                <div className='mb-4 flex items-center justify-between gap-4'>
                    <div>
                        <h1 className='text-foreground text-lg font-semibold'>
                            내 상담 이력
                        </h1>
                        <p className='text-muted-foreground mt-0.5 text-xs'>
                            {items ? `${items.length}건` : '불러오는 중'}
                        </p>
                    </div>
                    <Link
                        to='/consultation/chat'
                        className='border-border bg-background text-foreground hover:border-primary hover:text-primary inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors'>
                        <Plus className='size-4' />새 상담
                    </Link>
                </div>

                {error ? <ErrorBanner className='mb-3'>{error}</ErrorBanner> : null}

                {items === null ? (
                    <p className='text-muted-foreground text-sm'>
                        상담 이력을 불러오는 중…
                    </p>
                ) : items.length === 0 ? (
                    <EmptyState
                        title='아직 상담 이력이 없어요'
                        description='HACCP AI 상담챗봇에서 질문하면 여기에서 다시 볼 수 있어요.'
                        action={
                            <Link
                                to='/consultation/chat'
                                className='text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline'>
                                상담 시작하기
                                <ArrowRight className='size-4' />
                            </Link>
                        }
                    />
                ) : (
                    <ul className='space-y-2'>
                        {items.map((session) => (
                            <li key={session.id}>
                                <Link
                                    to={`/sessions/${session.id}`}
                                    className='border-border bg-card hover:border-primary group flex items-center justify-between gap-3 rounded-md border p-4 transition-colors'>
                                    <div className='flex min-w-0 items-start gap-2.5'>
                                        <MessageSquare className='text-muted-foreground mt-0.5 size-4 shrink-0' />
                                        <div className='min-w-0'>
                                            <p className='text-foreground truncate text-sm font-medium'>
                                                {session.title ||
                                                    '제목 없는 상담'}
                                            </p>
                                            <p className='text-muted-foreground mt-1 text-xs'>
                                                최근 대화{' '}
                                                {formatDateTime(
                                                    session.updatedAt
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className='flex shrink-0 items-center gap-2'>
                                        <Badge tone='info'>상담</Badge>
                                        <ArrowRight className='text-muted-foreground group-hover:text-primary size-4' />
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </AppShell>
    )
}
